import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, Upload, Loader2, Download, Trash2, FileText, Music, Save } from "lucide-react";
import { toast } from "sonner";
import CaseStageTracker, { STAGES } from "../components/CaseStageTracker";

function formatBytes(b) {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
}

export default function AdminClientDetail() {
  const { clientId } = useParams();
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [category, setCategory] = useState("document");
  const fileRef = useRef(null);

  // editable profile state
  const [edit, setEdit] = useState(null);

  // Memoize non-admin users used to populate the linked-account dropdown.
  // Prevents recomputing filter on every render.
  const linkableUsers = useMemo(
    () => users.filter((u) => u.role !== "admin"),
    [users],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, u] = await Promise.all([
        api.get(`/admin/clients/${clientId}`),
        api.get("/admin/users"),
      ]);
      setData(c.data);
      setUsers(u.data);
      setEdit({
        full_name: c.data.client.full_name,
        email: c.data.client.email || "",
        phone: c.data.client.phone || "",
        case_number: c.data.client.case_number || "",
        notes: c.data.client.notes || "",
        status: c.data.client.status || "active",
        case_stage: c.data.client.case_stage || "intake",
        user_id: c.data.client.user_id || "",
      });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.put(`/admin/clients/${clientId}`, {
        ...edit,
        user_id: edit.user_id || null,
      });
      toast.success("Profile updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingProfile(false); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error("File exceeds 50MB limit");
      e.target.value = "";
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    try {
      await api.post(`/admin/clients/${clientId}/files`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("File uploaded");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const download = async (file) => {
    try {
      const resp = await api.get(`/admin/files/${file.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = file.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteFile = async (id) => {
    if (!confirm("Delete this file?")) return;
    try { await api.delete(`/admin/files/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-400" /></div>;
  }
  if (!data || !edit) return null;
  const { client, files } = data;

  return (
    <div className="bg-black min-h-screen" data-testid="client-detail-page">
      <div className="container-pl py-12">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-amber-400 mb-8" data-testid="back-to-admin">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back to admin
        </Link>

        {/* Case stage tracker */}
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-8 mb-8" data-testid="admin-stage-card">
          <div className="flex justify-between items-baseline flex-wrap gap-3 mb-2">
            <div>
              <p className="overline mb-2">Case progress</p>
              <h1 className="display-serif text-2xl text-zinc-50">{client.full_name}</h1>
            </div>
            <div className="flex items-center gap-3">
              <Label className="text-xs">Stage</Label>
              <Select value={edit.case_stage} onValueChange={(v) => setEdit({ ...edit, case_stage: v })}>
                <SelectTrigger className="rounded-sm w-56 h-10" data-testid="case-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-8">
            <CaseStageTracker stage={edit.case_stage} />
          </div>
        </div>

        {/* Profile editor */}
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-8 mb-8" data-testid="profile-editor">
          <div className="flex justify-between items-baseline mb-6">
            <p className="overline">Client profile</p>
            <Button onClick={saveProfile} disabled={savingProfile} className="rounded-sm bg-blue-700 hover:bg-blue-600" data-testid="save-profile-btn">
              {savingProfile ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" strokeWidth={1.5} />}
              Save changes
            </Button>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div><Label>Full name</Label><Input value={edit.full_name} onChange={(e) => setEdit({ ...edit, full_name: e.target.value })} className="rounded-sm mt-1.5" /></div>
            <div><Label>Case #</Label><Input value={edit.case_number} onChange={(e) => setEdit({ ...edit, case_number: e.target.value })} className="rounded-sm mt-1.5" /></div>
            <div><Label>Email</Label><Input value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} className="rounded-sm mt-1.5" /></div>
            <div><Label>Phone</Label><Input value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} className="rounded-sm mt-1.5" /></div>
            <div>
              <Label>Status</Label>
              <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}>
                <SelectTrigger className="rounded-sm mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Linked client account (for portal access)</Label>
              <Select value={edit.user_id || "none"} onValueChange={(v) => setEdit({ ...edit, user_id: v === "none" ? "" : v })}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="link-user-select"><SelectValue placeholder="Not linked" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Not linked —</SelectItem>
                  {linkableUsers.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label>Notes (visible to client in their portal)</Label>
              <Textarea value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} className="rounded-sm mt-1.5 min-h-[120px]" />
            </div>
          </div>
        </div>

        {/* Files */}
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm">
          <div className="p-5 border-b border-zinc-800 flex flex-wrap items-center justify-between gap-3">
            <p className="overline">Documents & recordings</p>
            <div className="flex items-center gap-2">
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="rounded-sm w-40 h-10" data-testid="file-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="document">Document</SelectItem>
                  <SelectItem value="audio">Audio Recording</SelectItem>
                </SelectContent>
              </Select>
              <input ref={fileRef} type="file" className="hidden" onChange={onUpload} data-testid="file-input" />
              <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-sm bg-blue-700 hover:bg-blue-600" data-testid="upload-file-btn">
                {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" strokeWidth={1.5} />}
                Upload
              </Button>
            </div>
          </div>
          {files.length === 0 ? (
            <div className="p-12 text-center text-sm text-zinc-400" data-testid="no-files">No files uploaded yet.</div>
          ) : (
            <Table data-testid="files-table">
              <TableHeader>
                <TableRow>
                  <TableHead>File</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {files.map((f) => (
                  <TableRow key={f.id} data-testid={`file-row-${f.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {f.category === "audio" ? <Music className="h-4 w-4 text-amber-400" strokeWidth={1.5} /> : <FileText className="h-4 w-4 text-amber-400" strokeWidth={1.5} />}
                        {f.original_filename}
                      </div>
                    </TableCell>
                    <TableCell><Badge variant="outline" className="rounded-sm text-xs">{f.category}</Badge></TableCell>
                    <TableCell className="text-sm text-zinc-300">{formatBytes(f.size)}</TableCell>
                    <TableCell className="text-sm text-zinc-300">{new Date(f.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <Button onClick={() => download(f)} variant="ghost" size="sm" className="rounded-sm" data-testid={`download-${f.id}`}>
                          <Download className="h-4 w-4" strokeWidth={1.5} />
                        </Button>
                        <Button onClick={() => deleteFile(f.id)} variant="ghost" size="sm" className="rounded-sm text-red-600 hover:text-red-700" data-testid={`delete-file-${f.id}`}>
                          <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}
